import { ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import { IGaugeSettings } from "qpr-base/configuration/types";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import { formatPerformanceValue } from "utils/utils";
import HighChart from "./Highchart";
import { IChartSelectEvent } from "./types";

const usePerformanceDonutStyles = makeThemedStyles(
  "PerformanceDonut",
  (theme) => ({
    container: {
      display: "flex",
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
    },
    chartTitleStyle: {
      fontFamily: "Roboto, Helvetica",
      fontSize: "18px",
      fontWeight: 500,
      color: "rgb(84,84,84)",
      "& span": { fontSize: "12px" },
    },
  })
);

export const PerformanceDonut = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren;
      settings: IGaugeSettings;
      angleStart?: number;
      angleEnd?: number;
      width?: number;
      height?: string | number;
      onSelect?: (value: IChartSelectEvent) => void;
      disableAnimation?: boolean;
    }>
  ) => {
    const styles = usePerformanceDonutStyles();
    const { element: el, settings } = props;

    const valueKey: keyof ICardElementWithChildren = settings.valueAttribute
      ? (settings.valueAttribute as keyof ICardElementWithChildren)
      : "PERF_YTD";

    const rangeColor = settings.colorAttribute
      ? el[settings.colorAttribute as keyof ICardElementWithChildren]
      : el["rangecolor"];

    const startAngle = props.angleStart || 0;
    const endAngle = props.angleEnd || 360;

    const getElementValue = (
      setting: { value?: Number; attribute?: string } | undefined,
      defaultValue: Number
    ): Number => {
      if (!setting) return defaultValue;
      if (setting.value) return Number(setting.value);
      if (setting.attribute && setting.attribute in el) {
        const val = el[setting.attribute as keyof ICardElementWithChildren];
        if (val) return Number(val);
      }
      return defaultValue;
    };

    const getUnit = (
      setting: { value?: string; attribute?: string } | undefined,
      defaultUnit: string
    ): string => {
      if (!setting) return defaultUnit;
      if (setting.value) return setting.value;
      if (setting.attribute && setting.attribute in el) {
        const val: any =
          el[setting.attribute as keyof ICardElementWithChildren];
        if (val) return val;
      }
      return defaultUnit;
    };

    const hcOptions = React.useMemo(() => {
      if (!el) {
        return {};
      }
      return {
        credits: {
          enabled: false,
        },
        chart: {
          width: props.width || 120,
          type: "solidgauge",
          height: props.height || "110%",
          backgroundColor: "rgba(255,255,255,0)",
        },
        title: {
          useHTML: true,
          verticalAlign: "middle",
          floating: true,
          text: `<div class="${styles.container}"><div class="${
            styles.chartTitleStyle
          }">${formatPerformanceValue(el[valueKey])} <span>${getUnit(
            settings.unit,
            ""
          )}</span></div></div>`,
        },
        pane: {
          startAngle: startAngle,
          endAngle: endAngle,
          background: [
            {
              outerRadius: "112%",
              innerRadius: "83%",
              backgroundColor: "#33333322",
              borderWidth: 0,
              shape: "arc",
            },
          ],
        },
        yAxis: {
          min: getElementValue(settings.minValue, 0),
          max: getElementValue(settings.maxValue, 100),
          lineWidth: 0,
          tickPositions: [],
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              enabled: false,
            },
            animation: !props.disableAnimation,
            linecap: "round",
            stickyTracking: false,
            rounded: false,
          },
        },
        tooltip: {
          useHTML: true,
          formatter: function () {
            return `${t(settings.title || "Actual")} ${
              el[valueKey as keyof ICardElementWithChildren]
            }`;
          },
        },
        series: [
          {
            name: settings.title || "Actual",
            events: {
              click: function (this: any, event: any) {
                if (props.onSelect) {
                  props.onSelect({
                    category: event.point.category,
                    index: this.index,
                    name: this.name,
                    items: [props.element],
                  });
                }
              },
            },
            data: [
              {
                color: {
                  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                  stops: [
                    ...(el[valueKey as keyof ICardElementWithChildren]
                      ? [
                          [0, `#${rangeColor || "gray"}ff`],
                          [1, `#${rangeColor || "gray"}99`],
                        ]
                      : [
                          [0, `#${rangeColor || "gray"}00`],
                          [1, `#${rangeColor || "gray"}00`],
                        ]),
                  ],
                },
                radius: "112%",
                innerRadius: "83%",
                y: Number(el[valueKey as keyof ICardElementWithChildren] || 0),
              },
            ],
          },
        ],
      };
    }, [el]);

    if (!el) return null;
    return <HighChart chartOptions={hcOptions} />;
  }
);
